import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import {
  MainTableFilterModel,
  PrintReportFilterModel,
} from 'src/app/shared/models/main-table-filter.model';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { filterAllDepsToken } from 'src/app/shared/functions/filter-all-deps-token';
import { IdName } from 'src/app/shared/models/id-name';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IDelegateNonConformanceRequest } from '../models/delegate-non-conformance-request';
import { IDelegateNonConformanceModel } from '../models/delegate-non-conformance.model';
import { IListNonConformanceModel } from '../models/list-non-conformance.model';
import { INonConformanceForIO } from '../models/non-conformance-for-io.model';
import { INonConformanceRequest, INonConformanceResponse } from '../models/non-conformance.model';
import { IPrintNonConformanceModel } from '../models/print-non-conformance.model';
import { NonConformanceStatus } from '../enums/non-conformance-status.enum';
import { ICustomNotificationRecipients, IdComment } from '../models/id-comment.model';
import { INonConformanceVersion } from '../models/non-conformance-version.model';
import { FormDataService } from '../../shared/attachments/service/form-data.service';
import { ISignNonConformanceModel } from '../models/sign-non-conformance.model';
import { INonConformanceStateCountResponse } from '../models/non-conformance-state-count-response.model';
import { INonConformanceReportsStatusResponse } from '../models/non-conformance-reports-status-response.model';
import { INonConformanceOpenReportsResponse } from '../models/non-conformance-open-reports-response.model';
import { ISubtask, ISubtaskRequest } from '../models/subtask.model';
import { IWindForce } from '../models/wind-force.model';
import { NonConformanceActionType } from '../enums/non-conformance-action-type.enum';
import { INotificationRecipientsResponse } from '../models/notification-recipients.model';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  type: 'Type.Name',
  severity: 'Severity.Name',
  responsibleForFollowUpPositions: 'PositionsResponsibleForFollowUp.Name',
  responsibleForFollowUpPositionGroups: 'PositionGroupsResponsibleForFollowUp.Name',
  responsibleForFollowUpRoles: 'RolesResponsibleForFollowUp.Name',
  responsibleForFollowUpUsers: 'UsersResponsibleForFollowUp.FullName',
  responsibleForClosingPositions: 'PositionsResponsibleForClosing.Name',
  responsibleForClosingPositionGroups: 'PositionGroupsResponsibleForClosing.Name',
  responsibleForClosingRoles: 'RolesResponsibleForClosing.Name',
  responsibleForClosingUsers: 'UsersResponsibleForClosing.FullName',
  department: 'Department.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  responsibleForFollowUpPositions: 'responsibleForFollowUp',
  responsibleForFollowUpPositionGroups: 'responsibleForFollowUp',
  responsibleForFollowUpRoles: 'responsibleForFollowUp',
  responsibleForFollowUpUsers: 'responsibleForFollowUp',
  responsibleForClosingPositions: 'responsibleForClosing',
  responsibleForClosingPositionGroups: 'responsibleForClosing',
  responsibleForClosingRoles: 'responsibleForClosing',
  responsibleForClosingUsers: 'responsibleForClosing',
};

@Injectable({
  providedIn: 'root',
})
export class NonConformanceApiService {
  private readonly headers = { 'content-type': 'application/json' };
  private get baseUrl(): string {
    return this.getDepartmentBaseUrl();
  }
  private get allDepsUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/NonConformance`;
  }

  private get nonConformancesForIOUrl(): string {
    return `${this.baseUrl}/Interconnected`;
  }

  private getDepartmentBaseUrl(departmentId?: string): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${departmentId || getDepartmentId()}/NonConformance`;
  }

  constructor(
    private http: HttpClient,
    private store: Store
  ) {}

  getAllNonConformancesByState(
    state: NonConformanceStatus,
    currentFilter?: MainTableFilterModel,
    allDeps?: boolean,
    relatedNonConformancesOnly?: boolean
  ): Observable<ApiResponse<IListNonConformanceModel[]>> {
    let params = createFilterParams(currentFilter);
    let url = '';

    if (allDeps) {
      if (state === NonConformanceStatus.Deleted) {
        params = params.set('IsDeleted', 'true');
      } else {
        params = params.set('States', state);
      }
      url = this.allDepsUrl;
      params = params.set('ExcludeArchived', 'true');

      if (relatedNonConformancesOnly !== undefined) {
        params = params.set('RelatedNonConformancesOnly', String(relatedNonConformancesOnly));
      }

      params = MainTableFilteringHelperService.appendFilteringParams(
        params,
        currentFilter,
        COLUMN_TO_KEY_MAP,
        OR_GROUP_MAP
      );

      return this.http.get<ApiResponse<IListNonConformanceModel[]>>(url, { params });
    }

    if (state === NonConformanceStatus.Deleted) {
      params = params.set('IsDeleted', 'true');
      url = `${this.baseUrl}`;
    } else {
      params = params.set('State', state);
      url = `${this.baseUrl}/State`;
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<ApiResponse<IListNonConformanceModel[]>>(url, { params });
  }

  getAllNonConformancesWithStates(
    states: NonConformanceStatus[],
    currentFilter?: MainTableFilterModel,
    allDeps = true,
    departmentIds?: string[]
  ): Observable<ApiResponse<IListNonConformanceModel[]>> {
    if (states.includes(NonConformanceStatus.Deleted)) {
      throw new Error(
        'Deleted non-conformances are not supported in getAllNonConformancesWithStates().'
      );
    }
    let params = createFilterParams(currentFilter);
    let url = allDeps ? this.allDepsUrl : this.baseUrl;

    states.forEach((state) => {
      params = params.append('States', state);
    });

    const filteredDepartmentIds = filterAllDepsToken(departmentIds);
    if (allDeps && filteredDepartmentIds.length > 0) {
      filteredDepartmentIds.forEach((id) => {
        params = params.append('DepartmentIds', id);
      });
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<ApiResponse<IListNonConformanceModel[]>>(url, { params });
  }

  getNonConformanceById(id: string, ncDepartmentId?: string): Observable<INonConformanceResponse> {
    const url = `${this.getDepartmentBaseUrl(ncDepartmentId)}/${id}`;
    return this.http.get<INonConformanceResponse>(url);
  }

  getNonConformancesForIO(): Observable<INonConformanceForIO[]> {
    return this.http.get<INonConformanceForIO[]>(this.nonConformancesForIOUrl);
  }

  createNonConformance(
    nonConformance: INonConformanceRequest
  ): Observable<INonConformanceResponse> {
    const formData = this.toNonConformanceFormData(nonConformance);
    return this.http.post<INonConformanceResponse>(this.baseUrl, formData);
  }

  updateNonConformance(
    nonConformance: INonConformanceRequest
  ): Observable<INonConformanceResponse> {
    const formData = this.toNonConformanceFormData(nonConformance);
    return this.http.put<INonConformanceResponse>(this.baseUrl, formData);
  }

  deleteNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/${request.id}`;
    const params = new HttpParams({
      fromObject: {
        Comment: request.comment,
        ...this.mapNotificationRecipients(request.customNotificationRecipients),
      },
    });
    return this.http.delete<void>(url, { params });
  }

  private toNonConformanceFormData(nonConformance: INonConformanceRequest): FormData {
    const { customNotificationRecipients, ...report } = nonConformance;
    return FormDataService.mapToFormData({
      ...report,
      ...this.mapNotificationRecipients(customNotificationRecipients),
    });
  }

  private mapNotificationRecipients(
    recipients?: ICustomNotificationRecipients
  ): Record<string, boolean | string[]> {
    const customRecipients = recipients?.useCustomRecipients ? recipients : undefined;
    return {
      'CustomNotificationRecipients.UseCustomRecipients': recipients?.useCustomRecipients ?? false,
      'CustomNotificationRecipients.UserIds': customRecipients?.userIds ?? [],
      'CustomNotificationRecipients.PositionIds': customRecipients?.positionIds ?? [],
      'CustomNotificationRecipients.PositionGroupIds': customRecipients?.positionGroupIds ?? [],
      'CustomNotificationRecipients.RoleIds': customRecipients?.roleIds ?? [],
    };
  }

  delegateNonConformance(
    delegateNonConformance: IDelegateNonConformanceRequest
  ): Observable<IDelegateNonConformanceModel> {
    const url = `${this.baseUrl}/Delegate`;
    return this.http.put<IDelegateNonConformanceModel>(url, delegateNonConformance);
  }

  closeNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Close`;
    return this.http.put<void>(url, request);
  }

  reopenNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Reopen`;
    return this.http.put<void>(url, request);
  }

  restoreNonConformance(request: IdComment, ncDepartmentId?: string): Observable<void> {
    const url = `${this.getDepartmentBaseUrl(ncDepartmentId)}/${request.id}/Restore`;
    return this.http.put<void>(url, request);
  }

  completeNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Complete`;
    return this.http.put<void>(url, request);
  }

  rejectNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Reject`;
    return this.http.put<void>(url, request);
  }

  getNonConformanceLogs(id: string): Observable<INonConformanceVersion[]> {
    const url = `${this.baseUrl}/${id}/Log`;
    return this.http.get<INonConformanceVersion[]>(url);
  }

  signCompletedNonConformance(request: ISignNonConformanceModel): Observable<void> {
    const url = `${this.baseUrl}/Sign`;
    return this.http.put<void>(url, request);
  }

  downloadNonConformanceFile(printNonConformance: IPrintNonConformanceModel): Observable<Blob> {
    let url = `${this.baseUrl}/${printNonConformance.id}/Print`;
    if (
      printNonConformance.includeComments ||
      printNonConformance.includeDetails ||
      printNonConformance.listAttachments ||
      printNonConformance.includeSubtasks
    ) {
      url += `?IncludeDetails=${printNonConformance.includeDetails}&IncludeComments=${printNonConformance.includeComments}&IncludeAttachments=${printNonConformance.listAttachments}&IncludeSubtasks=${printNonConformance.includeSubtasks}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  getNonConformanceStateCounts(): Observable<INonConformanceStateCountResponse> {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    let url = '';
    let params = new HttpParams();
    if (allDeps) {
      url = `${this.allDepsUrl}/EachStateCount`;
      params = params.set('ExcludeArchived', 'true');
    } else {
      url = `${this.baseUrl}/EachStateCount`;
    }

    return this.http.get<INonConformanceStateCountResponse>(url, { params });
  }

  getReportsStatus(
    departmentIds?: string[],
    from?: string,
    to?: string
  ): Observable<INonConformanceReportsStatusResponse> {
    const url = `${this.allDepsUrl}/ReportsStatus`;
    let params: { [key: string]: string | string[] } = {};
    const filteredDepartmentIds = filterAllDepsToken(departmentIds);

    if (filteredDepartmentIds.length > 0) {
      params['DepartmentIds'] = filteredDepartmentIds;
    }

    if (from) {
      params['From'] = from;
    }

    if (to) {
      params['To'] = to;
    }

    return this.http.get<INonConformanceReportsStatusResponse>(url, { params });
  }

  getOpenReportsStatus(
    severityIds?: string[],
    departmentIds?: string[]
  ): Observable<INonConformanceOpenReportsResponse> {
    const url = `${this.allDepsUrl}/OpenReports`;
    let params: { [key: string]: string | string[] } = {};
    const filteredDepartmentIds = filterAllDepsToken(departmentIds);

    if (severityIds && severityIds.length > 0) {
      params['SeverityIds'] = severityIds;
    }

    if (filteredDepartmentIds.length > 0) {
      params['DepartmentIds'] = filteredDepartmentIds;
    }

    return this.http.get<INonConformanceOpenReportsResponse>(url, { params });
  }

  printReport(
    filter?: PrintReportFilterModel,
    menuId?: string,
    status?: NonConformanceStatus
  ): Observable<Blob> {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    let url = '';
    if (allDeps) {
      url = `${this.allDepsUrl}/Report`;
    } else {
      url = `${this.baseUrl}/Report`;
    }

    let params = createFilterParams(filter);
    if (menuId) {
      params = params.set('MenuId', menuId);
    }
    if (status === NonConformanceStatus.Deleted) {
      params = params.set('IsDeleted', 'true');
    } else if (status) {
      params = params.set('States', `${[status]}`);
    }

    if (filter) {
      params = MainTableFilteringHelperService.appendFilteringParams(
        params,
        filter,
        COLUMN_TO_KEY_MAP,
        OR_GROUP_MAP
      );
    }

    return this.http.get(url, { params, responseType: 'blob' });
  }

  getSubtask(ncId: string, subtaskId: string): Observable<ISubtask> {
    const url = `${this.baseUrl}/${ncId}/Subtask/${subtaskId}`;
    return this.http.get<ISubtask>(url);
  }

  createSubtask(ncId: string, subtask: ISubtaskRequest): Observable<ISubtask> {
    const url = `${this.baseUrl}/${ncId}/Subtask`;
    const formData = FormDataService.mapToFormData(subtask);
    return this.http.post<ISubtask>(url, formData);
  }

  updateSubtask(ncId: string, subtaskId: string, subtask: ISubtaskRequest): Observable<ISubtask> {
    const url = `${this.baseUrl}/${ncId}/Subtask/${subtaskId}`;
    const formData = FormDataService.mapToFormData(subtask);
    return this.http.put<ISubtask>(url, formData);
  }

  deleteSubtask(ncId: string, subtaskId: string): Observable<void> {
    const url = `${this.baseUrl}/${ncId}/Subtask/${subtaskId}`;
    return this.http.delete<void>(url);
  }

  getSubtasksLogs(ncId: string): Observable<ISubtask[]> {
    const url = `${this.baseUrl}/${ncId}/Subtask/Log`;
    return this.http.get<ISubtask[]>(url);
  }

  getWindForces(): Observable<IWindForce[]> {
    return this.http.get<IWindForce[]>(`${environment.apiUrl}/api/windForces`);
  }

  getWindDirections(): Observable<IdName[]> {
    return this.http.get<IdName[]>(`${environment.apiUrl}/api/windDirections`);
  }

  getNotificationRecipients(
    id: string,
    actionType: NonConformanceActionType,
    departmentId?: string
  ): Observable<INotificationRecipientsResponse> {
    const url = `${this.getDepartmentBaseUrl(departmentId)}/${id}/NotificationRecipients`;
    return this.http.get<INotificationRecipientsResponse>(url, {
      params: { ActionType: actionType.toString() },
    });
  }
}
